import { useEffect, useState, type ReactNode } from 'react';
import type { GatePage, RegistrationStatus, SurveyConfig } from '../types';
import { Blocks } from '../components/Blocks';
import { optOutSurvey } from '../api';
import { buildEventVars, resolveStepTwoCopy } from '../lib/stepTwo';

interface Props {
  gatePage: GatePage;
  surveyConfig?: SurveyConfig;
  resumedSubmissionId?: string;
  registrationStatus: RegistrationStatus | null;
  leadFirstName: string | null;
  eventConfig?: Record<string, unknown>;
  onSurveyOptOut: (status: RegistrationStatus) => void;
  children: ReactNode;
}

/**
 * "Step 2 of 2" around the survey questions, for a registrant whose seat is
 * already saved (gatePage.stepTwo). The confirmation is one quiet line; the
 * question leads. Why this exists: lib/stepTwo.ts.
 */
export default function StepTwoFrame({
  gatePage,
  surveyConfig,
  resumedSubmissionId,
  registrationStatus,
  leadFirstName,
  eventConfig,
  onSurveyOptOut,
  children,
}: Props) {
  const copy = resolveStepTwoCopy(gatePage, buildEventVars(eventConfig, leadFirstName));
  const [optOutPending, setOptOutPending] = useState(false);
  const [optOutError, setOptOutError] = useState<string | null>(null);

  // Arriving from the registration form's redirect, start at the top so the
  // seat-saved line and the first question are the first thing on screen.
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Same rule as the gate page's opt-out: only an optional survey offers it,
  // and only when there is a saved registration to mark.
  const canOptOut =
    registrationStatus === 'registered-pending-survey' &&
    surveyConfig?.required === false &&
    !!resumedSubmissionId;

  async function handleOptOut() {
    if (!resumedSubmissionId) return;
    setOptOutPending(true);
    setOptOutError(null);
    try {
      const r = await optOutSurvey(resumedSubmissionId);
      onSurveyOptOut(r.status);
    } catch (err) {
      setOptOutError(err instanceof Error ? err.message : 'Could not save that. Please try again.');
      setOptOutPending(false);
    }
  }

  return (
    <div className="step-two">
      <div className="step-two-head">
        <div className="step-two-saved">
          <span className="step-two-check" aria-hidden="true">✓</span> {copy.seatSaved}
        </div>
        <div className="step-two-label">{copy.stepLabel}</div>
        {copy.intro && <div className="step-two-intro">{copy.intro}</div>}
      </div>
      {children}
      {canOptOut && (
        <div className="step-two-skip">
          <button type="button" className="btn btn-link" onClick={handleOptOut} disabled={optOutPending}>
            {optOutPending ? 'Saving…' : copy.skipLabel}
          </button>
          {optOutError && <div className="step-two-error" role="alert">{optOutError}</div>}
        </div>
      )}
      {copy.belowBlocks.length > 0 && (
        <div className="step-two-below">
          <Blocks blocks={copy.belowBlocks} />
        </div>
      )}
    </div>
  );
}
